// Pure helpers for staged label-format import (7.6.22 Slice 3). Ports legacy
// `labelFormatStorage.js`'s ZPL dimension sniff so the import modal can pre-fill
// paperSize for Zebra uploads without a round trip to the server.

export interface ZebraDimensions {
  readonly widthIn: number
  readonly heightIn: number
}

const PW_RE = /\^PW(\d+)/
const LL_RE = /\^LL0*(\d+)/

/**
 * Reads a ZPL body's `^PW` (print width) and `^LL` (label length) directives
 * — both in dots — and converts to inches at the given DPI, rounded to 2 dp.
 * Returns `null` if either directive is absent (legacy parity: a partial read
 * is not actionable, so the caller falls back to manual paper-size entry).
 */
export function inferZebraDimensions(body: string, dpi = 203): ZebraDimensions | null {
  const pwMatch = PW_RE.exec(body)
  const llMatch = LL_RE.exec(body)
  if (!pwMatch || !llMatch) return null

  const widthDots = Number(pwMatch[1])
  const heightDots = Number(llMatch[1])
  if (!Number.isFinite(widthDots) || !Number.isFinite(heightDots) || dpi <= 0) return null

  return {
    widthIn: Math.round((widthDots / dpi) * 100) / 100,
    heightIn: Math.round((heightDots / dpi) * 100) / 100,
  }
}

function formatDimension(n: number): string {
  // Strip a trailing ".00"/".50" -> "2"/"2.5" so whole-inch sizes read as the
  // existing "zebra_2x1" convention rather than "zebra_2.00x1.00".
  return Number(n.toFixed(2)).toString()
}

/** Formats inferred dimensions as the `zebra_<w>x<h>` paperSize convention used elsewhere. */
export function formatZebraPaperSize(dims: ZebraDimensions): string {
  return `zebra_${formatDimension(dims.widthIn)}x${formatDimension(dims.heightIn)}`
}
